// Reglas de la pantalla Modelia: etiquetas, detección de la contradicción decisión↔SCI, filtros
// y agrupación de los parámetros del modelo. Puras y sin React.
import type { ParameterResponse } from '@fiado/type-kit/bin/loanConfig/dtos/responses/ParameterResponse';
import { ParameterTypeEnum } from '@fiado/type-kit/bin/loanConfig/enums/ParameterTypeEnum';
import { ParameterValueTypeEnum } from '@fiado/type-kit/bin/loanConfig/enums/ParameterValueTypeEnum';
import type { SciEvaluationResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/SciEvaluationResponse';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { SciLayer2SourceEnum } from '@fiado/type-kit/bin/loanScoring/enums/SciLayer2SourceEnum';
import { ScoringDecisionEnum } from '@fiado/type-kit/bin/loanScoring/enums/ScoringDecisionEnum';
import { ScoringProviderEnum } from '@fiado/type-kit/bin/loanScoring/enums/ScoringProviderEnum';
import { ApiError, userMessageOf } from '@/api/errors';
import { normalizeForSearch } from '@/lib/dataTable';
import {
    ANY_FILTER,
    type ModeliaEvaluationFilters,
    type ModeliaEvaluationRow,
    type ModeliaListCoverage,
    type ModeliaReadState,
    type ScoringParameterGroup,
    type ScoringParameterItem,
} from './types';

/** Lo que se pinta donde no hay dato: em dash, nunca vacío ni un cero que parezca medido. */
export const NO_VALUE = '—';

/** Escala del SCI que viaja en el contrato. El 0-1000 del proveedor ya llega normalizado aquí. */
export const SCI_SCALE_LABEL = 'escala 0-100';

/** El `code` con el que el silo dice que el rol no alcanza. Reintentar no lo cambia. */
const PERMISSION_DENIED = 'PERMISSION_DENIED';

/** Un 403 se lee distinto que una caída: no se ofrece reintentar lo que no va a cambiar. */
export function modeliaReadStateOf(error: unknown): 'error' | 'forbidden' {
    return error instanceof ApiError && error.code === PERMISSION_DENIED ? 'forbidden' : 'error';
}

/** El estado de la pantalla: cargando, con error, leído-y-vacío o leído-con-datos. */
export function resolveReadState(input: {
    isLoading: boolean;
    error: unknown;
    count: number;
}): ModeliaReadState {
    if (input.isLoading) return 'loading';
    if (input.error !== null && input.error !== undefined) return modeliaReadStateOf(input.error);
    return input.count === 0 ? 'empty' : 'ready';
}

// ---------- Etiquetas ----------

/** Nivel derivado del SCI, en el copy de SureKeep. NO es el nivel de cuenta de la wallet. */
export const CLIENT_LEVEL_LABEL: Record<ClientLevelEnum, string> = {
    [ClientLevelEnum.GOLD]: 'Oro',
    [ClientLevelEnum.SILVER]: 'Plata',
    [ClientLevelEnum.BRONZE]: 'Bronce',
    [ClientLevelEnum.NOT_ELIGIBLE]: 'No elegible',
};

export const SCORING_DECISION_LABEL: Record<ScoringDecisionEnum, string> = {
    [ScoringDecisionEnum.APPROVED]: 'Aprobada',
    [ScoringDecisionEnum.REJECTED]: 'Rechazada',
    [ScoringDecisionEnum.NO_ELIGIBLE]: 'Sin oferta',
};

export const SCORING_PROVIDER_LABEL: Record<ScoringProviderEnum, string> = {
    [ScoringProviderEnum.MODELIAS]: 'Modelias',
    [ScoringProviderEnum.CIRCULO]: 'Círculo de Crédito',
};

export const LAYER2_SOURCE_LABEL: Record<SciLayer2SourceEnum, string> = {
    [SciLayer2SourceEnum.FICO]: 'FICO · historial pleno en buró',
    [SciLayer2SourceEnum.FIS]: 'FIS · thin file con score de inclusión',
    [SciLayer2SourceEnum.NEUTRAL]: 'Neutral · sin dato de buró',
};

/** Nombre legible de cada sub-score de las capas; una clave nueva se muestra tal cual llegó. */
export const BREAKDOWN_COMPONENT_LABEL: Record<string, string> = {
    fico: 'FICO',
    // El type-kit documenta `worstDelinquency` y el motor manda `delinquency`: se aceptan las dos.
    worstDelinquency: 'Peor atraso',
    delinquency: 'Peor atraso',
    openCredits: 'Créditos abiertos',
    historyAge: 'Antigüedad del historial',
    inquiries: 'Consultas al buró',
    onTime: 'Pagos a tiempo',
    avgDelay: 'Atraso promedio',
    episodes: 'Episodios de mora',
    restructures: 'Reestructuras',
    completed: 'Créditos liquidados',
    writeOff: 'Castigos',
};

export function componentLabel(key: string): string {
    return BREAKDOWN_COMPONENT_LABEL[key] ?? key;
}

// ---------- La contradicción ----------

/**
 * Una evaluación aprobada cuyo score o nivel no la sostiene. Es un defecto REAL de los datos: la
 * pantalla lo señala tal cual llegó, no lo normaliza ni lo esconde.
 */
export function isContradictoryEvaluation(evaluation: SciEvaluationResponse): boolean {
    return (
        evaluation.decision === ScoringDecisionEnum.APPROVED &&
        (evaluation.level === ClientLevelEnum.NOT_ELIGIBLE || evaluation.sciTotal === 0)
    );
}

/** Qué se contradice con qué, con las palabras que el operador necesita para reportarlo. */
export function explainContradiction(evaluation: SciEvaluationResponse): string | null {
    if (!isContradictoryEvaluation(evaluation)) return null;
    const reasons: string[] = [];
    if (evaluation.level === ClientLevelEnum.NOT_ELIGIBLE) {
        reasons.push(`el nivel es «${CLIENT_LEVEL_LABEL[ClientLevelEnum.NOT_ELIGIBLE]}»`);
    }
    if (evaluation.sciTotal === 0) reasons.push('el SCI es 0');
    return `La decisión llegó aprobada, pero ${reasons.join(' y ')}. Los tres valores se muestran tal como los entregó el motor.`;
}

// ---------- Volver a correr el análisis ----------

/** Si se puede volver a correr, y con qué palabras se explica que no. */
export interface ModeliaRerunGate {
    canRerun: boolean;
    /** El motivo accesible del botón bloqueado; `null` cuando sí se puede. */
    blockedReason: string | null;
}

/** Espeja `EVALUATION_WITHOUT_CUSTOMER`: sin cliente el motor no tiene a quién consultar. */
export const RERUN_BLOCKED_WITHOUT_CUSTOMER =
    'Esta evaluación no tiene cliente registrado: es anterior al campo y el motor no tiene a quién volver a consultar en el padrón.';

/**
 * El estado del botón para una fila. Cualquier decisión se vuelve a correr: el único bloqueo es
 * la evaluación sin cliente, porque sin ese id el motor no tiene a quién pedirle la CURP.
 */
export function resolveRerunGate(row: ModeliaEvaluationRow): ModeliaRerunGate {
    if (row.retailCustomerId === null) {
        return { canRerun: false, blockedReason: RERUN_BLOCKED_WITHOUT_CUSTOMER };
    }
    return { canRerun: true, blockedReason: null };
}

/** Qué hace la pantalla con un `rerun` que falló. */
export interface ModeliaRerunFailure {
    message: string;
    /** La fila que se ve ya no describe lo que hay en el motor: hay que releer el listado. */
    refreshList: boolean;
    /** Solo lo transitorio se reintenta. Un dato malo no cambia por insistir. */
    retryable: boolean;
}

interface RerunFailureRule {
    message: string;
    refreshList: boolean;
    retryable: boolean;
}

/**
 * Los códigos que devuelve `POST /scoring/evaluations/{id}/rerun`. El 502 del padrón es el único
 * que se reintenta: culpar a los datos del cliente porque el padrón estaba caído es el peor caso.
 */
const RERUN_FAILURE_BY_CODE: Record<string, RerunFailureRule> = {
    SCI_EVALUATION_NOT_FOUND: {
        message: 'Esta evaluación ya no existe en el motor. Actualizamos la lista.',
        refreshList: true,
        retryable: false,
    },
    EVALUATION_WITHOUT_CUSTOMER: {
        message: RERUN_BLOCKED_WITHOUT_CUSTOMER,
        refreshList: false,
        retryable: false,
    },
    CUSTOMER_WITHOUT_CURP: {
        message:
            'El cliente no tiene CURP en su expediente. Complétalo desde su ficha y vuelve a intentarlo.',
        refreshList: false,
        retryable: false,
    },
    RETAIL_CUSTOMER_UNAVAILABLE: {
        message:
            'No pudimos consultar el padrón de clientes en este momento. Es una falla temporal, no un dato inválido.',
        refreshList: false,
        retryable: true,
    },
    [PERMISSION_DENIED]: {
        message:
            'No tienes el permiso loan.scoring.write, que es el que deja volver a correr el análisis. Pídeselo a tu administrador para tu rol.',
        refreshList: false,
        retryable: false,
    },
};

export function describeRerunFailure(error: unknown): ModeliaRerunFailure {
    const rule = error instanceof ApiError ? RERUN_FAILURE_BY_CODE[error.code] : undefined;
    if (rule === undefined) {
        return { message: userMessageOf(error), refreshList: false, retryable: false };
    }
    return rule;
}

// ---------- Lista ----------

function normalize(value: string): string {
    return normalizeForSearch(value.trim());
}

/** Solo dígitos y sin el prefijo de país: `+52 55 1234 5678` y `5512345678` comparan igual. */
function normalizePhone(value: string): string {
    return value.replace(/\D/g, '').replace(/^521?(?=\d{10}$)/, '');
}

/**
 * Barre el nombre del acreditado y los dos identificadores. El teléfono se compara solo si la fila
 * lo trae: hoy el listado del motor no lo devuelve y buscar por teléfono no encuentra nada.
 */
export function matchesSearch(row: ModeliaEvaluationRow, search: string): boolean {
    const needle = normalize(search);
    if (!needle) return true;

    const { customerName, customerPhone, evaluation } = row;
    if (
        normalize(evaluation.evaluationId).includes(needle) ||
        normalize(evaluation.borrowerId).includes(needle) ||
        (customerName !== null && normalize(customerName).includes(needle))
    ) {
        return true;
    }

    // Sin dígitos en la búsqueda no hay teléfono que comparar: un needle vacío matchearía con todo.
    const phoneNeedle = normalizePhone(search);
    if (phoneNeedle === '' || customerPhone === null) return false;
    return normalizePhone(customerPhone).includes(phoneNeedle);
}

/** El predicado de una fila contra el panel de filtros. Lo consume el modelo filtrado de la tabla. */
export function matchesEvaluationFilters(
    row: ModeliaEvaluationRow,
    filters: ModeliaEvaluationFilters,
): boolean {
    const { evaluation } = row;
    if (filters.decision !== ANY_FILTER && evaluation.decision !== filters.decision) return false;
    if (filters.level !== ANY_FILTER && evaluation.level !== filters.level) return false;
    if (filters.provider !== ANY_FILTER && evaluation.provider !== filters.provider) return false;
    if (filters.thinFile !== ANY_FILTER && evaluation.thinFile !== (filters.thinFile === 'thin')) {
        return false;
    }
    if (filters.onlyContradictory && !isContradictoryEvaluation(evaluation)) return false;
    return matchesSearch(row, filters.search);
}

export function filterEvaluations(
    rows: readonly ModeliaEvaluationRow[],
    filters: ModeliaEvaluationFilters,
): ModeliaEvaluationRow[] {
    return rows.filter((row) => matchesEvaluationFilters(row, filters));
}

/** ¿Alguna fila cargada trae teléfono? Decide si la búsqueda puede prometerlo o tiene que aclararlo. */
export function anyRowHasPhone(rows: readonly ModeliaEvaluationRow[]): boolean {
    return rows.some((row) => row.customerPhone !== null);
}

export function emptyEvaluationFilters(): ModeliaEvaluationFilters {
    return {
        search: '',
        decision: ANY_FILTER,
        level: ANY_FILTER,
        provider: ANY_FILTER,
        thinFile: ANY_FILTER,
        onlyContradictory: false,
    };
}

/** Cuántos filtros están puestos, para el conteo del encabezado de la columna. */
export function countActiveFilters(filters: ModeliaEvaluationFilters): number {
    const picked = [filters.decision, filters.level, filters.provider, filters.thinFile].filter(
        (value) => value !== ANY_FILTER,
    ).length;
    const search = filters.search.trim() === '' ? 0 : 1;
    return picked + search + (filters.onlyContradictory ? 1 : 0);
}

// ---------- Parámetros del modelo ----------

interface ScoringGroupDefinition {
    key: string;
    title: string;
    hint: string;
    /** Suma que el grupo debería dar; `null` cuando no es un juego de pesos. */
    expectedSum: number | null;
    keys: readonly string[];
}

/** Los 17 parámetros del SCI, en los cuatro grupos con los que se leen. */
export const SCORING_PARAMETER_GROUPS: readonly ScoringGroupDefinition[] = [
    {
        key: 'layer2Weights',
        title: 'Pesos de Capa 2 (buró)',
        hint: 'Cómo se reparte el score de buró entre sus cinco variables.',
        expectedSum: 1,
        keys: [
            'l2_w_fico',
            'l2_w_delinquency',
            'l2_w_open_credits',
            'l2_w_history_age',
            'l2_w_inquiries',
        ],
    },
    {
        key: 'layer3Weights',
        title: 'Pesos de Capa 3 (historial interno)',
        hint: 'Cómo se reparte el score del historial en SureKeep entre sus seis variables.',
        expectedSum: 1,
        keys: [
            'l3_w_on_time',
            'l3_w_avg_delay',
            'l3_w_episodes',
            'l3_w_restructures',
            'l3_w_completed',
            'l3_w_write_off',
        ],
    },
    {
        key: 'layerMix',
        title: 'Mezcla de capas',
        hint: 'Cuánto pesa cada capa en el SCI final, según si el cliente es nuevo o recurrente.',
        expectedSum: null,
        keys: ['layer2_weight_new', 'layer2_weight_recurrent', 'layer3_weight_recurrent'],
    },
    {
        key: 'levelCuts',
        title: 'Cortes de nivel',
        hint: 'Los umbrales de SCI que definen Oro, Plata y el piso de elegibilidad.',
        expectedSum: null,
        keys: ['level_gold_min', 'level_silver_min', 'sci_min_eligible'],
    },
];

/** Grupo de recogida: un parámetro SCORING que el backend agregue no se pierde de vista. */
const OTHER_GROUP = {
    key: 'other',
    title: 'Otros parámetros de scoring',
    hint: 'Claves que el motor devolvió y que no pertenecen a ninguno de los grupos conocidos.',
} as const;

/** El string como número, o `null` cuando no representa uno. */
function parseNumeric(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}

/** Compara por NÚMERO cuando los dos lo son: `'0.40'` y `'0.4'` son el mismo peso. */
function valueDiffersFromDefault(value: string, defaultValue: string): boolean {
    const current = parseNumeric(value);
    const fallback = parseNumeric(defaultValue);
    if (current !== null && fallback !== null) return current !== fallback;
    return value.trim() !== defaultValue.trim();
}

/** El instante de la última edición. `loan-config` manda epoch en MILISEGUNDOS como string: crudo
 *  a `Date` da 1789. `null` cuando no se puede leer — no se inventa una fecha. */
export function parameterUpdatedAtIso(updatedAt: string): string | null {
    const trimmed = updatedAt.trim();
    if (trimmed === '') return null;
    const date = /^\d+$/.test(trimmed) ? new Date(Number(trimmed)) : new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function toParameterItem(parameter: ParameterResponse): ScoringParameterItem {
    // `effectiveValue` debería ser `value ?? defaultValue`, pero el wire lo manda vacío: sin este
    // respaldo la columna «Vigente» queda en blanco y todo parámetro se marca como cambiado.
    const value = parameter.effectiveValue || (parameter.value ?? parameter.defaultValue);
    return {
        key: parameter.key,
        value,
        defaultValue: parameter.defaultValue,
        differsFromDefault: valueDiffersFromDefault(value, parameter.defaultValue),
        valueType: parameter.valueType,
        description: parameter.description,
        updatedAtIso: parameterUpdatedAtIso(parameter.updatedAt),
        lastModifiedByUserId: parameter.lastModifiedByUserId,
    };
}

/** El valor como número, o `null` cuando el parámetro no es numérico o no se puede leer. */
export function numericValueOf(item: ScoringParameterItem): number | null {
    if (item.valueType !== ParameterValueTypeEnum.NUMBER) return null;
    return parseNumeric(item.value);
}

/** Redondeo a 4 decimales: sumar 0.4 + 0.25 + … en binario deja cola y ensuciaría la comparación. */
function roundWeight(value: number): number {
    return Math.round(value * 10_000) / 10_000;
}

/** La suma del grupo; `null` si no es un juego de pesos o si algún valor no es numérico. */
function sumOf(items: readonly ScoringParameterItem[], expectedSum: number | null): number | null {
    if (expectedSum === null || items.length === 0) return null;
    let total = 0;
    for (const item of items) {
        const value = numericValueOf(item);
        if (value === null) return null;
        total += value;
    }
    return roundWeight(total);
}

/**
 * Los parámetros `SCORING` repartidos en sus grupos, con la suma de cada juego de pesos ya
 * calculada. Lo que el backend no devolvió queda listado en `missingKeys`, no rellenado.
 */
export function groupScoringParameters(
    parameters: readonly ParameterResponse[],
): ScoringParameterGroup[] {
    const scoring = parameters.filter(
        (parameter) => parameter.paramType === ParameterTypeEnum.SCORING,
    );
    const byKey = new Map(scoring.map((parameter) => [parameter.key, parameter]));
    const claimed = new Set<string>();

    const groups: ScoringParameterGroup[] = SCORING_PARAMETER_GROUPS.map((definition) => {
        const items: ScoringParameterItem[] = [];
        const missingKeys: string[] = [];
        for (const key of definition.keys) {
            const parameter = byKey.get(key);
            if (parameter === undefined) {
                missingKeys.push(key);
                continue;
            }
            claimed.add(key);
            items.push(toParameterItem(parameter));
        }
        const sum = sumOf(items, definition.expectedSum);
        return {
            key: definition.key,
            title: definition.title,
            hint: definition.hint,
            expectedSum: definition.expectedSum,
            items,
            sum,
            sumMatchesExpected:
                sum === null || definition.expectedSum === null
                    ? null
                    : sum === definition.expectedSum,
            missingKeys,
        };
    });

    const extras = scoring
        .filter((parameter) => !claimed.has(parameter.key))
        .map(toParameterItem)
        .sort((a, b) => a.key.localeCompare(b.key));

    if (extras.length > 0) {
        groups.push({
            ...OTHER_GROUP,
            expectedSum: null,
            items: extras,
            sum: null,
            sumMatchesExpected: null,
            missingKeys: [],
        });
    }
    return groups;
}

/** El vacío del listado: se leyó el motor y no hay evaluaciones, no es que falte recorrer nada. */
export const EMPTY_EVALUATIONS_NOTE =
    'El motor de scoring no tiene ninguna evaluación registrada. En cuanto el wizard evalúe a un cliente, la verás aquí.';

/** Plural del sustantivo que acompaña a `count`: «1 evaluación» y «2 evaluaciones». */
function evaluationsWord(count: number): string {
    return count === 1 ? 'evaluación' : 'evaluaciones';
}

/** Qué le falta a la lista de lo que sí bajó. Cada causa aparte: se arreglan distinto. */
export function describeCoverageGaps(coverage: ModeliaListCoverage): string[] {
    const gaps: string[] = [];
    if (coverage.unresolvedCustomerCount > 0) {
        gaps.push(
            `A ${String(coverage.unresolvedCustomerCount)} ${evaluationsWord(coverage.unresolvedCustomerCount)} no se les pudo resolver el nombre del cliente: en la lista ves el id del acreditado.`,
        );
    }
    if (coverage.withoutCustomerCount > 0) {
        gaps.push(
            `${String(coverage.withoutCustomerCount)} ${evaluationsWord(coverage.withoutCustomerCount)} no tienen cliente registrado: son anteriores al campo y no hay a quién resolverles el nombre.`,
        );
    }
    return gaps;
}

/** Los parámetros que el motor debería tener y no devolvió, para decirlo de una vez en la pantalla. */
export function missingScoringKeys(groups: readonly ScoringParameterGroup[]): string[] {
    return groups.flatMap((group) => group.missingKeys);
}
